import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { ArrowLeft, AlertTriangle, ShieldOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DeleteAccount — Apple-compliant account deletion confirmation page.
 *
 * Flow: type DELETE + re-enter password → POST /api/auth/delete-account
 * On success: clear local storage, log out, redirect to landing with toast.
 */
export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, logout, activeRole } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Resolve the canonical Account-tab destination based on the current role.
  // We do NOT rely on browser history (navigate(-1)) because tab changes
  // inside the dashboards don't always push history entries.
  const accountTabPath =
    activeRole === 'vendor' || user?.role === 'vendor'
      ? '/vendor/dashboard?tab=account'
      : '/customer-dashboard?tab=profile';

  const goBackToAccount = () => navigate(accountTabPath);

  const canSubmit = confirmText === 'DELETE' && password.length > 0 && !submitting;

  const handleDelete = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/auth/delete-account`,
        { password, confirm: 'DELETE' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Wipe everything client-side
      try { localStorage.clear(); } catch (e) { /* ignore */ }
      try { sessionStorage.clear(); } catch (e) { /* ignore */ }
      // Best-effort logout (clears auth state in App context)
      try { if (typeof logout === 'function') logout(); } catch (e) { /* ignore */ }
      toast.success('Your account has been deleted.');
      // Send to landing — replace history so back button can't go back into the app
      navigate('/?deleted=1', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 409) {
        setError(detail || 'You have active bookings. Cancel or complete them before deleting your account.');
      } else if (status === 401) {
        setError('Password is incorrect.');
      } else if (status === 400) {
        setError(detail || 'Type "DELETE" to confirm.');
      } else if (status === 403) {
        // Already deleted — treat as success
        try { localStorage.clear(); } catch (_) {}
        try { sessionStorage.clear(); } catch (_) {}
        navigate('/?deleted=1', { replace: true });
        return;
      } else {
        setError(detail || 'Unable to delete account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-xl mx-auto">
        {/* Back link */}
        <button
          data-testid="delete-account-back-btn"
          onClick={goBackToAccount}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <Card className="overflow-hidden border-red-100">
          {/* Danger header */}
          <div className="bg-red-50 border-b border-red-100 p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-red-100 shrink-0">
                <ShieldOff className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-red-900">Delete Your Account</h1>
                <p className="text-sm text-red-700 mt-1">
                  This action cannot be undone. Please read carefully before continuing.
                </p>
              </div>
            </div>
          </div>

          {/* Consequences */}
          <div className="p-6 space-y-4">
            <div data-testid="delete-account-consequences" className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Your access will end immediately.</p>
                  <p className="text-gray-600">You'll be signed out of every device. Login attempts will be refused.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Your personal info will be removed.</p>
                  <p className="text-gray-600">Your name, email, phone number, password, and security answers will be erased from your account.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Active bookings must be resolved first.</p>
                  <p className="text-gray-600">If you have any pending or in-progress bookings, deletion will be blocked until they're cancelled or completed.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Past bookings remain in records.</p>
                  <p className="text-gray-600">Completed bookings, reviews, and payment history are kept for tax, dispute, and legal compliance — but no longer linked to your personal identity.</p>
                </div>
              </div>
            </div>

            {/* Confirm input */}
            <div className="pt-4 border-t">
              <Label htmlFor="confirm-input" className="text-sm font-medium">
                Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                id="confirm-input"
                data-testid="delete-account-confirm-input"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                autoCapitalize="characters"
                className="mt-2"
              />
            </div>

            {/* Password input */}
            <div>
              <Label htmlFor="confirm-password" className="text-sm font-medium">
                Re-enter your password
              </Label>
              <Input
                id="confirm-password"
                data-testid="delete-account-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your current password"
                autoComplete="current-password"
                className="mt-2"
              />
            </div>

            {error && (
              <div
                data-testid="delete-account-error"
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3"
              >
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="delete-account-cancel-btn"
                type="button"
                variant="outline"
                onClick={goBackToAccount}
                disabled={submitting}
                className="sm:flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="delete-account-confirm-btn"
                type="button"
                onClick={handleDelete}
                disabled={!canSubmit}
                className="sm:flex-1 bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                {submitting ? 'Deleting…' : 'Delete My Account'}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center pt-2">
              Logged in as {user?.email}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
